// InboxPage - Direct messaging inbox with conversation list and message thread
// Feature: Wallet-to-wallet messaging

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import {
  useInbox,
  useConversationMessages,
  useConversationWith,
  useSendMessage,
  useMarkConversationAsRead,
  useConversationPolling,
  useTypingIndicator,
  messageQueryKeys,
  type ConversationWithDetails,
  type Message,
} from '../hooks/useMessages';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { renderContentWithLinks } from '../utils/linkify';
import { NewConversationModal } from '../components/messaging/NewConversationModal';
import { TradeActivityCard } from '../components/messaging/TradeActivityCard';
import type { TradeAction } from '../api/messages';

// Format wallet address for display
function formatAddress(address: string, length = 8): string {
  if (!address) return 'Unknown';
  if (address.length <= length * 2) return address;
  return `${address.slice(0, length)}...${address.slice(-6)}`;
}

// Get display name or formatted address
function getDisplayName(address: string, displayName: string | null): string {
  return displayName || formatAddress(address);
}

// Check if a participant is a project (announcement channel)
function isProjectParticipant(participant: string): boolean {
  return participant.startsWith('project___');
}

// Check if a participant is admin (system notifications)
function isAdminParticipant(participant: string): boolean {
  return participant === 'admin';
}

// Check if conversation is one-way (no replies allowed)
function isOneWayConversation(participant: string): boolean {
  return isProjectParticipant(participant) || isAdminParticipant(participant);
}

// Extract project slug from participant string (for linking)
function extractProjectId(participant: string): string | null {
  if (!isProjectParticipant(participant)) return null;
  return participant.replace('project___', '');
}

export function InboxPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuthStore();
  const { error: showError } = useNotificationStore();

  // Selected conversation from URL
  const selectedConversationId = searchParams.get('conversation');

  // Start conversation with a specific address (from NFT/Wallet page links)
  const startWithAddress = searchParams.get('start');

  // New conversation modal
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);

  // Message input state
  const [messageContent, setMessageContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const prevMessageCountRef = useRef<number>(0);
  const prevTypingUsersRef = useRef<string[]>([]);

  // Store the pending conversation details for new conversations not yet in inbox
  // Using state instead of ref so React can track changes properly
  const [pendingConversation, setPendingConversation] = useState<{
    id: string;
    otherParticipant: string;
    conversation: any;
  } | null>(null);

  // Fetch inbox
  const { data: conversations, isLoading: loadingInbox, refetch: refetchInbox } = useInbox();

  // Get or start conversation with specific address
  const { data: startedConversation, isLoading: loadingStartConversation } = useConversationWith(
    startWithAddress && startWithAddress !== user?.xrpl_address ? startWithAddress : undefined
  );

  // Handle starting a new conversation
  useEffect(() => {
    if (startedConversation && startWithAddress && user?.xrpl_address) {
      // Store the conversation data before changing the URL
      const otherParticipant = startedConversation.participant_1 === user.xrpl_address
        ? startedConversation.participant_2
        : startedConversation.participant_1;
      setPendingConversation({
        id: startedConversation.id,
        otherParticipant,
        conversation: startedConversation,
      });
      // Navigate to the conversation and remove the start param
      setSearchParams({ conversation: startedConversation.id });
      // Refetch inbox to include the new conversation
      refetchInbox();
    }
  }, [startedConversation, startWithAddress, setSearchParams, refetchInbox, user?.xrpl_address]);

  // Fetch messages for selected conversation
  const {
    data: messages,
    isLoading: loadingMessages,
    refetch: refetchMessages,
  } = useConversationMessages(selectedConversationId || undefined);

  // Mutations
  const sendMessageMutation = useSendMessage();
  const markAsReadMutation = useMarkConversationAsRead();

  // Real-time polling for new messages and typing indicators (1 second interval)
  const { typingUsers } = useConversationPolling(selectedConversationId || undefined, {
    pollingInterval: 1000,
    enabled: !!selectedConversationId,
  });

  // Typing indicator
  const { sendTyping, stopTyping } = useTypingIndicator(selectedConversationId || undefined);

  // Get the selected conversation details from inbox, or use pending conversation
  // This handles the case where a new conversation was just started but the inbox
  // hasn't been refetched yet
  const selectedConversation = (() => {
    // First try to find in the conversations list (most common case)
    const fromInbox = conversations?.find((c) => c.id === selectedConversationId);
    if (fromInbox) {
      return fromInbox;
    }

    // If not found but we have a pending conversation that matches, use it
    if (pendingConversation && pendingConversation.id === selectedConversationId) {
      return {
        ...pendingConversation.conversation,
        other_participant: pendingConversation.otherParticipant,
        other_participant_display_name: null, // Will be populated after inbox refetch
        unread_count: 0,
      } as ConversationWithDetails;
    }

    return undefined;
  })();

  // Clear pending conversation once it appears in the inbox
  useEffect(() => {
    if (pendingConversation && conversations?.some(c => c.id === pendingConversation.id)) {
      setPendingConversation(null);
    }
  }, [pendingConversation, conversations]);

  // Create display list of conversations, including new conversation if not yet in inbox
  const displayConversations = (() => {
    // If we have a selectedConversation derived from startedConversation that's not in inbox,
    // prepend it to the list so it shows in the sidebar
    if (selectedConversation && conversations) {
      const existsInInbox = conversations.some(c => c.id === selectedConversation.id);
      if (!existsInInbox) {
        return [selectedConversation, ...conversations];
      }
    }
    return conversations;
  })();

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Mark conversation as read when selected
  useEffect(() => {
    if (selectedConversationId && (selectedConversation?.unread_count ?? 0) > 0) {
      markAsReadMutation.mutate(selectedConversationId);
    }
  }, [selectedConversationId, selectedConversation?.unread_count]);

  // Scroll to bottom helper - scrolls the container directly
  const scrollToBottom = useCallback((smooth = true) => {
    const container = messagesContainerRef.current;
    if (container) {
      if (smooth) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, []);

  // Scroll to bottom on initial load and when new messages arrive
  useEffect(() => {
    if (messages && messages.length > 0) {
      const currentCount = messages.length;
      const isInitialLoad = prevMessageCountRef.current === 0;
      const hasNewMessages = currentCount > prevMessageCountRef.current;

      if (isInitialLoad) {
        // Initial load - scroll immediately without animation
        setTimeout(() => scrollToBottom(false), 50);
      } else if (hasNewMessages) {
        // New message arrived - smooth scroll
        scrollToBottom(true);
      }
      prevMessageCountRef.current = currentCount;
    }
  }, [messages, scrollToBottom]);

  // Scroll to bottom when typing indicator appears
  useEffect(() => {
    if (typingUsers.length > 0 && prevTypingUsersRef.current.length === 0) {
      scrollToBottom(true);
    }
    prevTypingUsersRef.current = typingUsers;
  }, [typingUsers, scrollToBottom]);

  // Note: Real-time updates are handled by useConversationPolling above
  // which polls every 3 seconds for a more responsive experience

  // Select a conversation
  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      setSearchParams({ conversation: conversationId });
      setMessageContent('');
      // Reset message count ref when switching conversations
      prevMessageCountRef.current = 0;
    },
    [setSearchParams]
  );

  // Send message
  const handleSendMessage = async () => {
    if (!messageContent.trim() || !selectedConversationId || isSending) return;

    setIsSending(true);
    // Clear typing indicator when sending
    stopTyping();

    try {
      await sendMessageMutation.mutateAsync({
        conversationId: selectedConversationId,
        content: messageContent.trim(),
      });
      setMessageContent('');
      messageInputRef.current?.focus();
      // Scroll to bottom after sending - small delay to allow DOM update
      setTimeout(() => scrollToBottom(true), 100);
    } catch (error) {
      showError((error as Error).message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  // Handle Enter key to send
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Start new conversation from modal
  const handleStartNewConversation = (address: string) => {
    setShowNewConversationModal(false);
    navigate(`/inbox?start=${address}`);
  };

  // Handle input change - send typing indicator
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageContent(e.target.value);
    if (e.target.value.trim()) {
      sendTyping();
    }
  };

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col h-[calc(100vh-160px)] mobile:h-[calc(100vh-120px)] overflow-hidden">
        {/* Page header */}
        <div className="flex-shrink-0 flex w-full items-center justify-between gap-4 pb-4 border-b border-neutral-border">
          <div>
            <h1 className="text-heading-1 font-heading-1 text-default-font">
              Messages
            </h1>
            <p className="text-body font-body text-subtext-color">
              {displayConversations?.length || 0} conversation
              {(displayConversations?.length || 0) !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => setShowNewConversationModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-primary text-white text-body-bold font-body-bold hover:bg-brand-primary/90 transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span className="hidden mobile:inline">New Message</span>
          </button>
        </div>

        {/* Main content area */}
        <div className="flex flex-1 min-h-0 mt-4 overflow-hidden">
          {/* Conversation list (left side) */}
          <div className="w-full sm:w-80 flex-shrink-0 border-r border-neutral-border overflow-y-auto">
            {loadingInbox ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
              </div>
            ) : !displayConversations || displayConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <svg
                  className="h-12 w-12 text-subtext-color mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                <p className="text-body-bold font-body-bold text-default-font">
                  No messages yet
                </p>
                <p className="text-body font-body text-subtext-color mt-1">
                  Start a conversation by entering a wallet address or from an NFT page
                </p>
                <button
                  onClick={() => setShowNewConversationModal(true)}
                  className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-primary text-white text-body-bold font-body-bold hover:bg-brand-primary/90 transition-colors shadow-sm"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  New Message
                </button>
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {displayConversations.map((conversation) => {
                  const isProject = isProjectParticipant(conversation.other_participant);
                  const isAdmin = isAdminParticipant(conversation.other_participant);
                  const isOneWay = isOneWayConversation(conversation.other_participant);
                  return (
                    <button
                      key={conversation.id}
                      onClick={() => handleSelectConversation(conversation.id)}
                      className={`w-full p-3 text-left rounded-xl transition-all duration-200 ${
                        selectedConversationId === conversation.id
                          ? 'bg-brand-primary/20 ring-1 ring-brand-primary/50'
                          : 'hover:bg-neutral-100/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center min-w-0">
                          {/* Admin gets shield icon, project gets megaphone, regular gets avatar */}
                          <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm ${
                            isAdmin
                              ? 'bg-gradient-to-br from-blue-600 to-indigo-600'
                              : isProject
                              ? 'bg-gradient-to-br from-amber-500 to-orange-500'
                              : 'bg-gradient-to-br from-brand-primary to-brand-primary/70'
                          }`}>
                            {isAdmin ? (
                              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                            ) : isProject ? (
                              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                              </svg>
                            ) : (
                              <span className="text-white font-semibold text-sm">
                                {(
                                  conversation.other_participant_display_name ||
                                  conversation.other_participant
                                )
                                  .charAt(0)
                                  .toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div className="ml-3 min-w-0">
                            <p className={`text-body-bold font-body-bold truncate ${
                              selectedConversationId === conversation.id
                                ? 'text-default-font'
                                : 'text-default-font'
                            }`}>
                              {isAdmin
                                ? 'Admin'
                                : isProject
                                ? conversation.other_participant_display_name || 'Project Announcement'
                                : getDisplayName(
                                    conversation.other_participant,
                                    conversation.other_participant_display_name
                                  )}
                            </p>
                            {(conversation.last_message_preview || conversation.last_message_sender) && (
                              <p className={`text-caption font-caption truncate mt-0.5 ${
                                conversation.unread_count > 0
                                  ? 'text-default-font font-medium'
                                  : 'text-subtext-color'
                              }`}>
                                {!isOneWay && conversation.last_message_sender ===
                                user?.xrpl_address
                                  ? 'You: '
                                  : ''}
                                {conversation.last_message_preview || '[Trade Activity]'}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end ml-2 flex-shrink-0">
                          {conversation.last_message_at && (
                            <span className={`text-caption font-caption ${
                              conversation.unread_count > 0
                                ? 'text-brand-primary font-medium'
                                : 'text-subtext-color'
                            }`}>
                              {formatDistanceToNow(
                                new Date(conversation.last_message_at),
                                { addSuffix: false }
                              )}
                            </span>
                          )}
                          {conversation.unread_count > 0 && (
                            <span className="mt-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold bg-brand-primary text-white shadow-sm">
                              {conversation.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Message thread (right side) */}
          <div className="hidden sm:flex flex-1 flex-col min-w-0 overflow-hidden">
            {!selectedConversationId ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <svg
                  className="h-16 w-16 text-subtext-color mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                <p className="text-body-bold font-body-bold text-default-font">
                  Select a conversation
                </p>
                <p className="text-body font-body text-subtext-color mt-1">
                  Choose a conversation from the list to view messages
                </p>
                <button
                  onClick={() => setShowNewConversationModal(true)}
                  className="mt-3 text-body font-body text-brand-primary hover:underline"
                >
                  or start a new conversation
                </button>
              </div>
            ) : (
              <>
                {/* Conversation header */}
                {selectedConversation && (() => {
                  const isProject = isProjectParticipant(selectedConversation.other_participant);
                  const isAdmin = isAdminParticipant(selectedConversation.other_participant);
                  const projectSlug = selectedConversation.other_participant_project_slug;
                  return (
                    <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-neutral-border bg-default-background">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isAdmin
                            ? 'bg-gradient-to-br from-blue-600 to-indigo-600'
                            : isProject
                            ? 'bg-gradient-to-br from-amber-500 to-orange-500'
                            : 'bg-brand-primary'
                        }`}>
                          {isAdmin ? (
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                          ) : isProject ? (
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                            </svg>
                          ) : (
                            <span className="text-white font-semibold text-sm">
                              {(
                                selectedConversation.other_participant_display_name ||
                                selectedConversation.other_participant
                              )
                                .charAt(0)
                                .toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="ml-3">
                          {isAdmin ? (
                            <>
                              <span className="text-body-bold font-body-bold text-default-font">
                                Admin
                              </span>
                              <p className="text-caption font-caption text-subtext-color">
                                System Notifications
                              </p>
                            </>
                          ) : isProject ? (
                            <>
                              <span className="text-body-bold font-body-bold text-default-font">
                                {selectedConversation.other_participant_display_name || 'Project Announcement'}
                              </span>
                              <p className="text-caption font-caption text-subtext-color">
                                Project Announcements
                              </p>
                            </>
                          ) : (
                            <>
                              <Link
                                to={`/wallets/${selectedConversation.other_participant}`}
                                className="text-body-bold font-body-bold text-default-font hover:text-brand-primary transition-colors"
                              >
                                {getDisplayName(
                                  selectedConversation.other_participant,
                                  selectedConversation.other_participant_display_name
                                )}
                              </Link>
                              <p className="text-caption font-caption text-subtext-color">
                                {formatAddress(
                                  selectedConversation.other_participant,
                                  12
                                )}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      {isProject && projectSlug ? (
                        <Link
                          to={`/projects/${projectSlug}`}
                          className="text-caption font-caption text-brand-primary hover:underline"
                        >
                          View Project
                        </Link>
                      ) : !isAdmin && (
                        <Link
                          to={`/wallets/${selectedConversation.other_participant}`}
                          className="text-caption font-caption text-brand-primary hover:underline"
                        >
                          View Wallet
                        </Link>
                      )}
                    </div>
                  );
                })()}

                {/* Messages */}
                <div
                  ref={messagesContainerRef}
                  className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-transparent to-neutral-50/30"
                >
                  {loadingMessages ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                    </div>
                  ) : !messages || messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <p className="text-body font-body text-subtext-color">
                        No messages yet. Start the conversation!
                      </p>
                    </div>
                  ) : (
                    // Reverse to show oldest first
                    [...messages].reverse().map((message, index, arr) => {
                      // Handle trade activity messages differently
                      if (message.message_type === 'trade_activity' && message.trade_id && message.trade_action) {
                        return (
                          <TradeActivityCard
                            key={message.id}
                            tradeId={message.trade_id}
                            tradeVersion={message.trade_version}
                            tradeAction={message.trade_action as TradeAction}
                            senderAddress={message.sender_address}
                            currentUserAddress={user?.xrpl_address || ''}
                            senderDisplayName={message.sender_display_name}
                            content={message.content}
                            createdAt={message.created_at}
                          />
                        );
                      }

                      // Regular text message
                      const isOwnMessage =
                        message.sender_address === user?.xrpl_address;
                      const prevMessage = index > 0 ? arr[index - 1] : null;
                      const nextMessage = index < arr.length - 1 ? arr[index + 1] : null;
                      const isFirstInGroup = !prevMessage || prevMessage.sender_address !== message.sender_address || prevMessage.message_type === 'trade_activity';
                      const isLastInGroup = !nextMessage || nextMessage.sender_address !== message.sender_address || nextMessage.message_type === 'trade_activity';

                      return (
                        <div
                          key={message.id}
                          className={`flex ${
                            isOwnMessage ? 'justify-end' : 'justify-start'
                          } ${!isFirstInGroup ? 'mt-0.5' : ''}`}
                        >
                          <div
                            className={`relative max-w-[70%] px-4 py-2.5 shadow-sm ${
                              isOwnMessage
                                ? 'bg-gradient-to-br from-brand-primary to-brand-primary/90 text-white'
                                : 'bg-neutral-700 text-neutral-100'
                            } ${
                              // Bubble shape based on position in group
                              isOwnMessage
                                ? isFirstInGroup && isLastInGroup
                                  ? 'rounded-2xl rounded-br-md'
                                  : isFirstInGroup
                                  ? 'rounded-2xl rounded-br-md'
                                  : isLastInGroup
                                  ? 'rounded-2xl rounded-tr-md rounded-br-md'
                                  : 'rounded-2xl rounded-r-md'
                                : isFirstInGroup && isLastInGroup
                                ? 'rounded-2xl rounded-bl-md'
                                : isFirstInGroup
                                ? 'rounded-2xl rounded-bl-md'
                                : isLastInGroup
                                ? 'rounded-2xl rounded-tl-md rounded-bl-md'
                                : 'rounded-2xl rounded-l-md'
                            }`}
                          >
                            <p className="text-body font-body whitespace-pre-wrap break-words leading-relaxed">
                              {renderContentWithLinks(message.content)}
                            </p>
                            <p
                              className={`text-[10px] mt-1 text-right ${
                                isOwnMessage
                                  ? 'text-white/60'
                                  : 'text-neutral-400'
                              }`}
                            >
                              {formatDistanceToNow(
                                new Date(message.created_at),
                                { addSuffix: true }
                              )}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  {/* Typing indicator */}
                  {typingUsers.length > 0 && (
                    <div className="flex justify-start">
                      <div className="bg-neutral-700 text-neutral-300 rounded-2xl rounded-bl-md px-4 py-2.5 shadow-sm">
                        <div className="flex items-center gap-1.5">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                            <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                            <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                          </div>
                          <span className="text-caption font-caption ml-1">
                            {selectedConversation?.other_participant_display_name
                              ? selectedConversation.other_participant_display_name
                              : formatAddress(typingUsers[0], 6)} is typing
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Message input - disabled for admin/project (one-way channels) */}
                {selectedConversation && isOneWayConversation(selectedConversation.other_participant) ? (
                  <div className="flex-shrink-0 p-4 border-t border-neutral-border bg-neutral-50">
                    <div className="flex items-center justify-center gap-2 text-subtext-color">
                      {isAdminParticipant(selectedConversation.other_participant) ? (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                      )}
                      <p className="text-body font-body">
                        {isAdminParticipant(selectedConversation.other_participant)
                          ? 'This is a notification channel. Replies are not enabled.'
                          : 'This is an announcement channel. Replies are not enabled.'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex-shrink-0 p-4 border-t border-neutral-border bg-default-background">
                    <div className="flex items-end gap-3">
                      <div className="flex-1 relative">
                        <textarea
                          ref={messageInputRef}
                          value={messageContent}
                          onChange={handleInputChange}
                          onKeyDown={handleKeyDown}
                          placeholder="Type a message..."
                          rows={1}
                          className="w-full min-h-[44px] max-h-[120px] px-4 py-3 text-body font-body rounded-2xl border border-neutral-border bg-default-background text-default-font placeholder-subtext-color focus:outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 resize-none transition-all"
                          style={{
                            height: 'auto',
                            minHeight: '44px',
                          }}
                          onInput={(e) => {
                            const target = e.target as HTMLTextAreaElement;
                            target.style.height = 'auto';
                            target.style.height = `${Math.min(
                              target.scrollHeight,
                              120
                            )}px`;
                          }}
                        />
                      </div>
                      <button
                        onClick={handleSendMessage}
                        disabled={!messageContent.trim() || isSending}
                        className={`flex items-center justify-center w-11 h-11 rounded-full transition-all ${
                          messageContent.trim() && !isSending
                            ? 'bg-brand-primary hover:bg-brand-primary/90 text-white shadow-md hover:shadow-lg'
                            : 'bg-neutral-700 text-neutral-500 cursor-not-allowed'
                        }`}
                      >
                        {isSending ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        ) : (
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                          </svg>
                        )}
                      </button>
                    </div>
                    <p className="text-[11px] text-neutral-500 mt-2 text-center">
                      Press Enter to send, Shift+Enter for new line
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Mobile: Show message thread when conversation selected */}
        {selectedConversationId && (
          <div className="fixed inset-0 z-50 bg-default-background sm:hidden flex flex-col">
            {/* Mobile header */}
            <div className="flex items-center p-4 border-b border-neutral-border">
              <button
                onClick={() => setSearchParams({})}
                className="mr-3 p-1 hover:bg-neutral-100 rounded-full"
              >
                <svg
                  className="h-6 w-6 text-default-font"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              {selectedConversation && (() => {
                const isProject = isProjectParticipant(selectedConversation.other_participant);
                const isAdmin = isAdminParticipant(selectedConversation.other_participant);
                const projectSlug = selectedConversation.other_participant_project_slug;
                const linkTo = isAdmin
                  ? null
                  : isProject && projectSlug
                  ? `/projects/${projectSlug}`
                  : `/wallets/${selectedConversation.other_participant}`;

                const content = (
                  <>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isAdmin
                        ? 'bg-gradient-to-br from-blue-600 to-indigo-600'
                        : isProject
                        ? 'bg-gradient-to-br from-amber-500 to-orange-500'
                        : 'bg-brand-primary'
                    }`}>
                      {isAdmin ? (
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      ) : isProject ? (
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                        </svg>
                      ) : (
                        <span className="text-white font-semibold text-xs">
                          {(
                            selectedConversation.other_participant_display_name ||
                            selectedConversation.other_participant
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                      )}
                    </div>
                    <span className="ml-2 text-body-bold font-body-bold text-default-font">
                      {isAdmin
                        ? 'Admin'
                        : isProject
                        ? selectedConversation.other_participant_display_name || 'Project Announcement'
                        : getDisplayName(
                            selectedConversation.other_participant,
                            selectedConversation.other_participant_display_name
                          )}
                    </span>
                  </>
                );

                return linkTo ? (
                  <Link to={linkTo} className="flex items-center">
                    {content}
                  </Link>
                ) : (
                  <div className="flex items-center">
                    {content}
                  </div>
                );
              })()}
            </div>

            {/* Mobile messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-transparent to-neutral-50/30">
              {loadingMessages ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                </div>
              ) : !messages || messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-body font-body text-subtext-color">
                    No messages yet. Start the conversation!
                  </p>
                </div>
              ) : (
                [...messages].reverse().map((message, index, arr) => {
                  // Handle trade activity messages differently
                  if (message.message_type === 'trade_activity' && message.trade_id && message.trade_action) {
                    return (
                      <TradeActivityCard
                        key={message.id}
                        tradeId={message.trade_id}
                        tradeVersion={message.trade_version}
                        tradeAction={message.trade_action as TradeAction}
                        senderAddress={message.sender_address}
                        currentUserAddress={user?.xrpl_address || ''}
                        senderDisplayName={message.sender_display_name}
                        content={message.content}
                        createdAt={message.created_at}
                      />
                    );
                  }

                  // Regular text message
                  const isOwnMessage =
                    message.sender_address === user?.xrpl_address;
                  const prevMessage = index > 0 ? arr[index - 1] : null;
                  const nextMessage = index < arr.length - 1 ? arr[index + 1] : null;
                  const isFirstInGroup = !prevMessage || prevMessage.sender_address !== message.sender_address || prevMessage.message_type === 'trade_activity';
                  const isLastInGroup = !nextMessage || nextMessage.sender_address !== message.sender_address || nextMessage.message_type === 'trade_activity';

                  return (
                    <div
                      key={message.id}
                      className={`flex ${
                        isOwnMessage ? 'justify-end' : 'justify-start'
                      } ${!isFirstInGroup ? 'mt-0.5' : ''}`}
                    >
                      <div
                        className={`relative max-w-[85%] px-4 py-2.5 shadow-sm ${
                          isOwnMessage
                            ? 'bg-gradient-to-br from-brand-primary to-brand-primary/90 text-white'
                            : 'bg-neutral-700 text-neutral-100'
                        } ${
                          isOwnMessage
                            ? isFirstInGroup && isLastInGroup
                              ? 'rounded-2xl rounded-br-md'
                              : isFirstInGroup
                              ? 'rounded-2xl rounded-br-md'
                              : isLastInGroup
                              ? 'rounded-2xl rounded-tr-md rounded-br-md'
                              : 'rounded-2xl rounded-r-md'
                            : isFirstInGroup && isLastInGroup
                            ? 'rounded-2xl rounded-bl-md'
                            : isFirstInGroup
                            ? 'rounded-2xl rounded-bl-md'
                            : isLastInGroup
                            ? 'rounded-2xl rounded-tl-md rounded-bl-md'
                            : 'rounded-2xl rounded-l-md'
                        }`}
                      >
                        <p className="text-body font-body whitespace-pre-wrap break-words leading-relaxed">
                          {renderContentWithLinks(message.content)}
                        </p>
                        <p
                          className={`text-[10px] mt-1 text-right ${
                            isOwnMessage
                              ? 'text-white/60'
                              : 'text-neutral-400'
                          }`}
                        >
                          {formatDistanceToNow(new Date(message.created_at), {
                            addSuffix: true,
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              {/* Typing indicator - mobile */}
              {typingUsers.length > 0 && (
                <div className="flex justify-start">
                  <div className="bg-neutral-700 text-neutral-300 rounded-2xl rounded-bl-md px-4 py-2.5 shadow-sm">
                    <div className="flex items-center gap-1.5">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                      <span className="text-caption font-caption ml-1">
                        {selectedConversation?.other_participant_display_name
                          ? selectedConversation.other_participant_display_name
                          : formatAddress(typingUsers[0], 6)} is typing
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile message input - disabled for admin/project (one-way channels) */}
            {selectedConversation && isOneWayConversation(selectedConversation.other_participant) ? (
              <div className="p-3 border-t border-neutral-border bg-neutral-50">
                <div className="flex items-center justify-center gap-2 text-subtext-color py-2">
                  {isAdminParticipant(selectedConversation.other_participant) ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                    </svg>
                  )}
                  <p className="text-caption font-caption">
                    {isAdminParticipant(selectedConversation.other_participant)
                      ? 'Notifications only'
                      : 'Announcements only'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-3 border-t border-neutral-border">
                <div className="flex items-end gap-2">
                  <textarea
                    value={messageContent}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    rows={1}
                    className="flex-1 min-h-[44px] max-h-[120px] px-4 py-3 text-body font-body rounded-2xl border border-neutral-border bg-default-background text-default-font placeholder-subtext-color focus:outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 resize-none transition-all"
                    style={{
                      height: 'auto',
                      minHeight: '44px',
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      target.style.height = 'auto';
                      target.style.height = `${Math.min(
                        target.scrollHeight,
                        120
                      )}px`;
                    }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!messageContent.trim() || isSending}
                    className={`flex items-center justify-center w-11 h-11 rounded-full transition-all flex-shrink-0 ${
                      messageContent.trim() && !isSending
                        ? 'bg-brand-primary hover:bg-brand-primary/90 text-white shadow-md'
                        : 'bg-neutral-700 text-neutral-500 cursor-not-allowed'
                    }`}
                  >
                    {isSending ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <NewConversationModal
        isOpen={showNewConversationModal}
        onClose={() => setShowNewConversationModal(false)}
        onStartConversation={handleStartNewConversation}
        currentUserAddress={user?.xrpl_address}
      />
    </DefaultPageLayout>
  );
}
